/**
 * Urun sayfasindaki koku secici.
 *
 * Duz bir metin kutusu yerine: ara, listeden sec, secilenler etiket olarak
 * dursun. Deger gizli bir input uzerinden ayni line item property'ye yazilir,
 * yani siparis ekraninda hicbir sey degismez.
 *
 * Arama/normallestirme mantigi koku-arama.js'teki KokuArama sinifindan gelir;
 * o dosya bundan once yuklenir (ikisi de defer, belge sirasi korunur).
 */
class KokuSecici extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-ks-input]');
    this.panel = this.querySelector('[data-ks-panel]');
    this.chipler = this.querySelector('[data-ks-chipler]');
    this.gizli = this.querySelector('[data-ks-gizli]');
    this.sayac = this.querySelector('[data-ks-sayac]');
    this.cubuk = this.querySelector('[data-ks-cubuk]');
    this.hata = this.querySelector('[data-ks-hata]');
    this.filtreler = this.querySelectorAll('[data-ks-filtre]');

    this.enCok = parseInt(this.dataset.enCok, 10) || 1;
    this.kaynak = this.dataset.kaynak === 'custom' ? 'custom' : 'stock';

    this.kayitlar = null; // null = henuz yuklenmedi
    this.stokKayitlari = []; // capraz kontrol icin (kisiye ozel)
    this.secilen = [];
    this.kategori = 'hepsi';
    this.secili = -1; // panelde klavyeyle gezilen satir
    this.zamanlayici = 0;
    this.yukleniyor = false;
  }

  connectedCallback() {
    // Bileşen görüş alanına yaklaşınca veriyi önden çek: müşteri kutuya
    // tıkladığında sonuçlar beklemeden gelsin. Dosya ~82KB, tek seferlik.
    if ('IntersectionObserver' in window) {
      const gozcu = new IntersectionObserver(
        (girdiler) => {
          if (!girdiler.some((g) => g.isIntersecting)) return;
          gozcu.disconnect();
          this.yukle();
        },
        { rootMargin: '400px' }
      );
      gozcu.observe(this);
    }

    // focus tek basina guvenilir degil (odak zaten kutudayken tekrar
    // tetiklenmez), o yuzden yukleme her giristen once de kontrol edilir.
    this.input.addEventListener('focus', () => {
      this.yukle().then(() => this.ciz());
    });
    this.input.addEventListener('input', () => {
      clearTimeout(this.zamanlayici);
      this.zamanlayici = setTimeout(() => this.yukle().then(() => this.ciz()), 110);
    });
    this.input.addEventListener('keydown', (e) => this.tus(e));

    this.panel.addEventListener('mousedown', (e) => {
      // mousedown: input blur olmadan once yakala
      const satir = e.target.closest('[data-ks-kod]');
      if (!satir) return;
      e.preventDefault();
      this.ekle(satir.dataset.ksKod);
    });

    this.chipler.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-ks-sil]');
      if (btn) this.cikar(btn.dataset.ksSil);
    });

    this.filtreler.forEach((b) =>
      b.addEventListener('click', () => {
        this.kategori = b.dataset.ksFiltre;
        this.filtreler.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        this.input.focus();
        this.yukle().then(() => this.ciz());
      })
    );

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.kapat();
    });

    // Asagidaki gezinme listesinden gelen ekleme istegi
    document.addEventListener('koku:ekle', (e) => {
      if (!e.detail || !e.detail.kod) return;
      if (e.detail.hedef && e.detail.hedef !== this.dataset.ksId) return;
      this.yukle().then(() => {
        this.ekle(e.detail.kod);
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    this.formaBagla();
    this.guncelle();
  }

  /* ---------- Veri ---------- */

  yukle() {
    if (this.kayitlar) return Promise.resolve();
    if (this.yukleniyor) return this.yukleniyor;

    // Onden yukleme sirasinda panel kendiliginden acilmasin; "yukleniyor"
    // mesaji yalnizca musteri kutuya girmisken anlamli.
    if (document.activeElement === this.input) {
      this.panelMesaj(this.dataset.yukleniyorMetin, 'ks__durum');
    }
    this.yukleniyor = fetch(this.dataset.veriUrl)
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then((j) => {
        this.kayitlar = (j[this.kaynak] || []).map(KokuSecici.dizinle);
        // Kisiye ozelde "bu koku zaten stokta" uyarisi icin stok da lazim.
        this.stokKayitlari = this.kaynak === 'custom' ? (j.stock || []).map(KokuSecici.dizinle) : [];
      })
      .catch((err) => {
        console.error('[koku-secici]', err);
        this.kayitlar = [];
        this.panelMesaj(this.dataset.hataMetin, 'ks__durum ks__durum--hata');
      });
    return this.yukleniyor;
  }

  static dizinle(x) {
    if (typeof KokuArama !== 'undefined') return { ...x, ...KokuArama.anahtarlar(x) };
    const q = KokuSecici.sadelestir(x.m + ' ' + x.b + ' ' + x.c);
    return { ...x, q, w: q.split(' '), mq: KokuSecici.sadelestir(x.m) };
  }

  static sadelestir(s) {
    if (typeof KokuArama !== 'undefined') return KokuArama.sadelestir(s);
    return String(s)
      .toLocaleLowerCase('tr')
      .replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g')
      .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c')
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  static kacis(s) {
    if (typeof KokuArama !== 'undefined') return KokuArama.kacis(s);
    const h = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => h[c]);
  }

  static vurgula(metin, sorgu) {
    if (typeof KokuArama !== 'undefined') return KokuArama.vurgula(metin, sorgu);
    return KokuSecici.kacis(metin);
  }

  /* ---------- Arama ---------- */

  kategoriGecer(k) {
    if (this.kategori === 'hepsi') return true;
    if (this.kategori === 'nis') return k.n === 1;
    return k.g === this.kategori;
  }

  eslesenler(ham) {
    const havuz = (this.kayitlar || []).filter(
      (k) => this.kategoriGecer(k) && !this.secilen.some((s) => s.c === k.c)
    );
    const sorgu = KokuSecici.sadelestir(ham);

    // Bos sorguda gezinilecek bir sey goster; bos panel cikmaz sokak.
    if (!sorgu) return havuz.slice(0, 8);
    if (typeof KokuArama === 'undefined') {
      return havuz.filter((k) => k.q.includes(sorgu)).slice(0, 8);
    }

    const kelimeler = sorgu.split(' ');
    const puanla = (k, fuzzy) => {
      let hata = 0;
      for (const w of kelimeler) {
        const d = KokuArama.gecerMi(k, w, fuzzy);
        if (d < 0) return -1;
        hata += d;
      }
      let p;
      if (k.mq === sorgu) p = 100;
      else if (k.mq.startsWith(sorgu)) p = 80;
      else if (k.mq.includes(sorgu)) p = 60;
      else if (k.q.startsWith(sorgu)) p = 50;
      else p = 30;
      if (KokuArama.onEkMi(k, kelimeler)) p += 12;
      return p - hata * 12 - Math.min(k.mq.length / 20, 5);
    };
    const tara = (fuzzy) =>
      havuz.map((k) => ({ k, p: puanla(k, fuzzy) }))
        .filter((x) => x.p >= 0)
        .sort((a, b) => b.p - a.p)
        .slice(0, 8)
        .map((x) => x.k);

    // Once birebir; sonuc yoksa yazim hatasina toleransli tekrar dene.
    const kesin = tara(false);
    return kesin.length ? kesin : tara(true);
  }

  /** Kisiye ozelde: aranan koku stok listesinde de var mi? */
  stoktaBul(ham) {
    if (this.dataset.stokUyari !== 'true') return null;
    if (!this.stokKayitlari.length) return null;
    const sorgu = KokuSecici.sadelestir(ham);
    if (sorgu.length < 3) return null;
    return this.stokKayitlari.find((k) => k.mq === sorgu || k.q.includes(sorgu)) || null;
  }

  /* ---------- Panel ---------- */

  ciz() {
    if (!this.kayitlar) return; // yukle() zaten mesaji basti
    if (this.dolu()) return this.kapat();

    const ham = this.input.value.trim();
    const liste = this.eslesenler(ham);

    if (!liste.length) {
      const html =
        '<li class="ks__durum"><p>' + KokuSecici.kacis(this.dataset.bosMetin) + '</p>' +
        (this.dataset.serbestIzin === 'true' && ham
          ? '<button type="button" class="ks__serbest" data-ks-kod="__serbest__">' +
            KokuSecici.kacis(this.dataset.serbestMetin).replace('{q}', KokuSecici.kacis(ham)) +
            '</button>'
          : '') +
        '</li>';
      this.panel.innerHTML = html;
      this.secili = -1;
      return this.ac();
    }

    const stok = this.stoktaBul(ham);
    let html = '';
    if (stok) {
      html +=
        '<li class="ks__uyari">' +
        '<strong>' + KokuSecici.kacis(this.dataset.stokBaslik) + '</strong>' +
        '<span>' + KokuSecici.kacis(stok.b + ' — ' + stok.m) + '</span>' +
        '<a href="' + KokuSecici.kacis(this.dataset.urunKok + stok.h) + '">' +
        KokuSecici.kacis(this.dataset.stokBaglanti) + '</a></li>';
    }

    html += liste
      .map(
        (k, i) =>
          '<li><button type="button" role="option" id="' + this.dataset.ksId + '-s' + i + '"' +
          ' class="ks__satir" data-ks-kod="' + KokuSecici.kacis(k.c) + '">' +
          '<span class="ks__satir-ad">' +
          '<span class="ks__marka">' + KokuSecici.vurgula(k.b, ham) + '</span>' +
          '<span class="ks__model">' + KokuSecici.vurgula(k.m, ham) + '</span></span>' +
          '<span class="ks__kod">' + KokuSecici.vurgula(k.c, ham) + '</span>' +
          '</button></li>'
      )
      .join('');

    this.panel.innerHTML = html;
    this.secili = -1;
    this.ac();
  }

  panelMesaj(metin, sinif) {
    this.panel.innerHTML = '<li class="' + sinif + '"><p>' + KokuSecici.kacis(metin) + '</p></li>';
    this.ac();
  }

  ac() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  kapat() {
    this.panel.hidden = true;
    this.panel.innerHTML = '';
    this.secili = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  tus(e) {
    const satirlar = [...this.panel.querySelectorAll('[data-ks-kod]')];
    if (e.key === 'Escape') return this.kapat();
    if (e.key === 'Enter') {
      if (this.secili >= 0 && satirlar[this.secili]) {
        e.preventDefault();
        this.ekle(satirlar[this.secili].dataset.ksKod);
      } else if (satirlar.length === 1) {
        e.preventDefault();
        this.ekle(satirlar[0].dataset.ksKod);
      } else {
        // Formu yanlislikla gondermesin
        e.preventDefault();
      }
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if (!satirlar.length) return;
    e.preventDefault();
    const yon = e.key === 'ArrowDown' ? 1 : -1;
    this.secili += yon;
    if (this.secili >= satirlar.length) this.secili = 0;
    if (this.secili < 0) this.secili = satirlar.length - 1;
    satirlar.forEach((s, i) => s.classList.toggle('ks__satir--imlec', i === this.secili));
    const etkin = satirlar[this.secili];
    if (etkin) {
      etkin.scrollIntoView({ block: 'nearest' });
      if (etkin.id) this.input.setAttribute('aria-activedescendant', etkin.id);
    }
  }

  /* ---------- Secim ---------- */

  dolu() {
    return this.secilen.length >= this.enCok;
  }

  ekle(kod) {
    if (this.dolu()) return;

    let kayit;
    if (kod === '__serbest__') {
      const ham = this.input.value.trim();
      if (!ham) return;
      kayit = { c: '', b: '', m: ham, serbest: true };
    } else {
      kayit = (this.kayitlar || []).find((k) => k.c === kod);
      if (!kayit) return;
      if (this.secilen.some((s) => s.c === kod)) return;
    }

    this.secilen.push(kayit);
    this.input.value = '';
    this.guncelle();
    this.kapat();
    if (!this.dolu()) this.input.focus();
  }

  cikar(kod) {
    const i = this.secilen.findIndex((s) => (s.c || s.m) === kod);
    if (i < 0) return;
    this.secilen.splice(i, 1);
    this.guncelle();
    this.input.focus();
  }

  /* ---------- Cikti ---------- */

  etiket(k) {
    if (k.serbest) return k.m;
    return k.c + ' – ' + k.b + ' ' + k.m;
  }

  guncelle() {
    const n = this.secilen.length;

    this.chipler.innerHTML = this.secilen
      .map((k) => {
        const anahtar = KokuSecici.kacis(k.c || k.m);
        return (
          '<li class="ks__chip' + (k.serbest ? ' ks__chip--serbest' : '') + '">' +
          (k.c ? '<span class="ks__chip-kod">' + KokuSecici.kacis(k.c) + '</span>' : '') +
          '<span class="ks__chip-ad">' +
          KokuSecici.kacis(k.serbest ? k.m : k.b + ' — ' + k.m) +
          '</span>' +
          '<button type="button" class="ks__chip-sil" data-ks-sil="' + anahtar + '"' +
          ' aria-label="' + KokuSecici.kacis(this.dataset.silMetin) + ' ' + anahtar + '">' +
          '<svg viewBox="0 0 12 13" aria-hidden="true" focusable="false"><path d="M8.486 9.33 2.828 3.67M2.885 9.385l5.544-5.77"/></svg>' +
          '</button></li>'
        );
      })
      .join('');

    this.gizli.value = this.secilen.map((k) => this.etiket(k)).join(', ');

    if (this.sayac) {
      const kalan = this.enCok - n;
      this.sayac.textContent =
        kalan > 0
          ? this.dataset.sayacMetin.replace('{n}', n).replace('{t}', this.enCok).replace('{k}', kalan)
          : this.dataset.tamamMetin.replace('{t}', this.enCok);
      this.sayac.classList.toggle('ks__sayac--tamam', kalan <= 0);
    }
    if (this.cubuk) this.cubuk.style.setProperty('--ks-oran', (n / this.enCok) * 100 + '%');

    this.toggleAttribute('data-dolu', this.dolu());
    this.input.disabled = this.dolu();
    this.input.placeholder = this.dolu() ? this.dataset.doluMetin : this.dataset.bosPlaceholder;

    if (n > 0) this.hataGoster('');
    this.dispatchEvent(new CustomEvent('koku-secici:degisti', { bubbles: true, detail: { secilen: this.secilen } }));
  }

  /* ---------- Form dogrulamasi ---------- */

  formaBagla() {
    const form = document.getElementById(this.dataset.formId);
    if (!form) return;
    // Dawn'in kendi submit dinleyicisinden once calismak icin belge
    // seviyesinde yakalama asamasinda dinliyoruz.
    document.addEventListener(
      'submit',
      (e) => {
        if (e.target !== form) return;
        if (this.secilen.length >= this.enCok) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.hataGoster(
          this.dataset.eksikMetin
            .replace('{t}', this.enCok)
            .replace('{k}', this.enCok - this.secilen.length)
        );
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.input.focus();
      },
      true
    );
  }

  hataGoster(metin) {
    if (!this.hata) return;
    this.hata.textContent = metin;
    this.hata.hidden = !metin;
    this.toggleAttribute('data-hatali', !!metin);
    if (metin) {
      this.classList.remove('ks--sarsil');
      void this.offsetWidth; // animasyonu yeniden tetikle
      this.classList.add('ks--sarsil');
    }
  }
}

customElements.define('koku-secici', KokuSecici);
